import { estJourOuvrable } from './dates';
import { hours } from './utils';

/**
 * Pause à déduire d'une journée pointée.
 *
 * La pause est celle du contrat de chacun — « certains font 45 minutes » —
 * à partir de son heure (12 h par défaut) : une journée qui traverse ce
 * créneau en perd le temps passé dedans. Avec une heure à midi, 7 h → 16 h 30
 * compte 8 h 30 et 7 h → 17 h 9 h ; avec 45 minutes, 7 h → 16 h 15 compte
 * 8 h 30. Une demi-journée qui ne touche pas la pause n'en perd rien.
 *
 * Une journée qui ne passe pas par midi (horaire décalé) garde le minimum de
 * la loi sur le travail (art. 15 LTr) : un quart d'heure au-delà de cinq
 * heures et demie, une demi-heure au-delà de sept, une heure au-delà de neuf.
 * Le bureau corrige la pause d'une journée dans la feuille d'heures quand
 * l'usage du chantier diffère.
 */

/** Pause de midi par défaut, quand le contrat n'en dit rien : une heure dès 12 h. */
export const PAUSE_MIDI = 60;
export const DEBUT_PAUSE_DEFAUT = '12:00';

/** Durée maximale d'une journée de travail prévue au contrat, en heures. */
export const JOURNEE_MAX = 9;

/** Minimum légal, du plus long au plus court : présence en heures, pause en minutes. */
const MINIMUM_LEGAL = [
  [9, 60],
  [7, 30],
  [5.5, 15],
];

const enMinutes = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  return m ? +m[1] * 60 + +m[2] : null;
};

/**
 * Pause du contrat d'un employé : son début et sa durée.
 *
 * @param {object} [emp] employé, avec `pauseDebut` et `pauseMin`
 * @returns {{debut: string, minutes: number}} la pause prévue
 */
export function pauseDuContrat(emp) {
  const minutes = Number(emp?.pauseMin);
  return {
    debut: emp?.pauseDebut || DEBUT_PAUSE_DEFAUT,
    minutes: Number.isFinite(minutes) && minutes >= 0 ? minutes : PAUSE_MIDI,
  };
}

/**
 * @param {string} entree heure d'arrivée, `HH:MM`
 * @param {string} sortie heure de départ, `HH:MM`
 * @param {object} [emp] employé dont le contrat fixe la pause ; absent, une heure dès 12 h
 * @returns {number} la pause à déduire, en minutes
 */
export function pauseLegale(entree, sortie, emp) {
  const debut = enMinutes(entree);
  const fin = enMinutes(sortie);
  if (debut == null || fin == null || fin <= debut) return 0;
  const contrat = pauseDuContrat(emp);
  const debutPause = enMinutes(contrat.debut) ?? 12 * 60;
  const finPause = debutPause + contrat.minutes;
  const pause = Math.max(0, Math.min(fin, finPause) - Math.max(debut, debutPause));
  // Le minimum légal se compte sur le temps de travail, pause déduite.
  const travail = (fin - debut - pause) / 60;
  const palier = MINIMUM_LEGAL.find(([seuil]) => travail > seuil);
  return Math.max(pause, palier ? palier[1] : 0);
}

/**
 * Pause à déduire d'un morceau de journée, quand l'ouvrier passe d'un chantier
 * à l'autre : la pause se compte une fois sur la journée entière. Le temps
 * passé entre deux chantiers vaut pause, et ce qui a déjà été déduit des
 * morceaux précédents ne l'est pas une seconde fois. Sans cela, un départ du
 * chantier A à 12 h 30 perdait la pause de midi, puis le chantier B la perdait
 * encore.
 *
 * @param {{entree: string, sortie: string, pause?: number}[]} dejaFaits morceaux
 *   de la journée déjà refermés
 * @param {string} entree arrivée du morceau qui se referme, `HH:MM`
 * @param {string} sortie départ du morceau qui se referme, `HH:MM`
 * @param {object} [emp] employé dont le contrat fixe la pause
 * @returns {number} la pause à déduire de ce morceau, en minutes
 */
export function pauseDuSegment(dejaFaits, entree, sortie, emp) {
  const faits = (dejaFaits || []).filter(
    (s) => enMinutes(s.entree) != null && enMinutes(s.sortie) != null,
  );
  if (faits.length === 0) return pauseLegale(entree, sortie, emp);
  const tous = [...faits, { entree, sortie, pause: 0 }].sort(
    (a, b) => enMinutes(a.entree) - enMinutes(b.entree),
  );
  const fin = Math.max(...tous.map((s) => enMinutes(s.sortie)));
  const finHM =
    String(Math.floor(fin / 60)).padStart(2, '0') + ':' + String(fin % 60).padStart(2, '0');
  const requise = pauseLegale(tous[0].entree, finHM, emp);
  let ecarts = 0;
  for (let i = 1; i < tous.length; i++) {
    ecarts += Math.max(0, enMinutes(tous[i].entree) - enMinutes(tous[i - 1].sortie));
  }
  const deduite = faits.reduce((t, s) => t + (Number(s.pause) || 0), 0);
  return Math.max(0, requise - ecarts - deduite);
}

/**
 * Heure de fin d'une journée du contrat : l'arrivée, les heures du jour et la
 * pause de midi. 07:00, 8 h 30 et une heure de pause donnent 16:30 ; avec
 * 45 minutes, 16:15.
 *
 * @param {string} entree heure d'arrivée, `HH:MM`
 * @param {number} [heuresJour] heures par jour du contrat
 * @param {object} [emp] employé dont le contrat fixe la pause
 * @returns {string} l'heure de départ, `HH:MM`
 */
export function finDeJournee(entree, heuresJour = 8, emp) {
  const [h, m] = String(entree || '07:00')
    .split(':')
    .map(Number);
  const jour = Math.min(Number(heuresJour) || 8, JOURNEE_MAX);
  const total = h * 60 + (m || 0) + Math.round(jour * 60) + pauseDuContrat(emp).minutes;
  return (
    String(Math.floor(total / 60) % 24).padStart(2, '0') + ':' + String(total % 60).padStart(2, '0')
  );
}

/**
 * Referme la journée ouverte d'un employé sur son heure de départ.
 *
 * <p>Une journée se pointe sur un chantier, ou sur la fiche de l'employé quand
 * aucun chantier n'est choisi. Les pointages d'un chantier nomment chacun leur
 * employé ; ceux de la fiche ne le nomment pas — ils sont tous à lui. La
 * clôture exigeait pourtant ce nom partout : relevé en production, une arrivée
 * pointée sans chantier ne se refermait plus dès que la page avait été
 * rechargée, et le bouton de départ restait sans effet.
 *
 * @param {object[]} pointages pointages d'un chantier, ou de la fiche
 * @param {object} journee journée à refermer
 * @param {string} journee.jour date du jour, au format ISO
 * @param {string} journee.sortie heure de départ, `HH:MM`
 * @param {number|string} [journee.empId] employé, pour les pointages d'un
 *        chantier ; absent pour ceux de la fiche
 * @param {object} [journee.complement] ce que le départ ajoute (position…)
 * @param {object} [journee.emp] employé, dont le contrat fixe la pause
 * @returns {object[]} les pointages, la journée ouverte refermée
 */
export function refermeLaJournee(pointages, { jour, sortie, empId, complement = {}, emp }) {
  const surLaFiche = empId == null;
  return (pointages || []).map((p) => {
    const aLui = surLaFiche ? p.chId == null : p.empId === empId;
    if (!aLui || p.date !== jour || !p.entree || p.sortie) return p;
    return { ...p, sortie, pause: pauseLegale(p.entree, sortie, emp), ...complement };
  });
}

/**
 * Corrige l'heure d'arrivée de la journée ouverte : arrivé plus tôt ou plus
 * tard que le moment où il a pointé, l'employé inscrit son heure réelle.
 *
 * @param {object[]} pointages pointages d'un chantier, ou de la fiche
 * @param {object} journee journée à corriger
 * @param {string} journee.jour date du jour, au format ISO
 * @param {string} journee.nouvelle heure d'arrivée réelle, `HH:MM`
 * @param {number|string} [journee.empId] employé, pour un chantier ; absent pour la fiche
 * @returns {object[]} les pointages, l'arrivée du jour corrigée
 */
export function corrigeLArrivee(pointages, { jour, nouvelle, empId }) {
  const surLaFiche = empId == null;
  return (pointages || []).map((p) => {
    const aLui = surLaFiche ? p.chId == null : p.empId === empId;
    if (!aLui || p.date !== jour || !p.entree || p.sortie) return p;
    return { ...p, entree: nouvelle, entreeCorrigee: true };
  });
}

/**
 * Une heure de départ se lit-elle après l'arrivée ? Le départ inscrit à la
 * main — plus tôt, plus tard — doit suivre l'arrivée du jour.
 *
 * @param {string} entree heure d'arrivée, `HH:MM`
 * @param {string} sortie heure de départ, `HH:MM`
 * @returns {boolean} vrai si le départ suit l'arrivée
 */
export function departApresArrivee(entree, sortie) {
  const e = enMinutes(entree);
  const s = enMinutes(sortie);
  return e != null && s != null && s > e;
}

/**
 * Total d'heures d'un relevé, jour par jour : la journée notée seulement dans
 * le rapport, sans pointage, compte aussi. Le total ne lisait que les
 * pointages, et ces heures paraissaient dans le détail sans entrer au total.
 *
 * @param {Array<{h: number}>} jours lignes du relevé
 * @returns {number} le total en heures décimales
 */
export function totalDesJours(jours) {
  return (jours || []).reduce((a, j) => a + (+j.h || 0), 0);
}

/**
 * Heures par semaine selon le contrat : la journée convenue (neuf heures au
 * plus, huit à défaut), cinq jours, au taux d'occupation (100 % à défaut).
 * Saisie à part, la semaine contredisait la journée.
 *
 * @param {number} heuresJour heures de travail par jour
 * @param {number} [taux] taux d'occupation en %
 * @returns {number} les heures par semaine, au centième
 */
export function heuresParSemaine(heuresJour, taux) {
  const jour = Math.min(+heuresJour > 0 ? +heuresJour : 8, JOURNEE_MAX);
  const t = +taux > 0 ? +taux : 100;
  return Math.round(jour * 5 * t) / 100;
}

/**
 * Heures de travail d'une journée du contrat : de l'arrivée à la fin, la
 * pause de midi déduite. La pause payée du matin (15 minutes à 9 h, selon la
 * convention) reste comptée. 07:00 → 16:30 avec une heure à midi : 8 h 30.
 *
 * @param {string} debut heure d'arrivée, `HH:MM`
 * @param {string} fin heure de fin, `HH:MM`
 * @param {number} [pauseMin] pause de midi, en minutes (une heure à défaut)
 * @returns {number|null} les heures, ou rien si l'horaire ne se lit pas
 */
export function journeeEntre(debut, fin, pauseMin = PAUSE_MIDI) {
  const d = enMinutes(debut);
  const f = enMinutes(fin);
  const pause = Number.isFinite(+pauseMin) ? +pauseMin : PAUSE_MIDI;
  if (d == null || f == null || f - d - pause <= 0) return null;
  return Math.round(((f - d - pause) / 60) * 100) / 100;
}

/**
 * Heures prévues par jour au contrat de l'employé : neuf au plus, huit à
 * défaut.
 *
 * @param {object} [emp] employé
 * @returns {number} les heures du jour
 */
export function heuresDuContrat(emp) {
  const h = Number(emp?.heuresJour);
  return Math.min(h > 0 ? h : 8, JOURNEE_MAX);
}

/**
 * L'écart d'une journée notée avec le contrat : l'ouvrier note ses heures
 * chaque jour, parce qu'il peut faire des heures sup ou partir plus tôt.
 *
 * @param {number} heures heures notées pour le jour
 * @param {object} [emp] employé, dont le contrat fixe la journée
 * @returns {number} l'écart en heures, positif pour des heures sup
 */
export function ecartDuJour(heures, emp) {
  return Math.round(((+heures || 0) - heuresDuContrat(emp)) * 100) / 100;
}

/**
 * Un écart lisible : « +1h00 », « −0h30 », « = » quand la journée est celle
 * du contrat.
 *
 * @param {number} ecart écart en heures
 * @returns {string} l'écart écrit
 */
export function fmtEcart(ecart) {
  const minutes = Math.round((+ecart || 0) * 60);
  if (minutes === 0) return '=';
  const a = Math.abs(minutes);
  return (minutes > 0 ? '+' : '−') + Math.floor(a / 60) + 'h' + String(a % 60).padStart(2, '0');
}

/**
 * Bilan d'un mois noté jour par jour, face au contrat : heures prévues,
 * heures sup et heures en moins. Une journée sur deux chantiers compte une
 * seule fois, pour le total de ses lignes. Seuls les jours du lundi au
 * vendredi sont prévus ; un samedi ou un dimanche travaillé est en plus.
 *
 * @param {Array<{k: string, h: number}>} lignes lignes du relevé, `k` le jour
 * @param {object} [emp] employé, dont le contrat fixe la journée
 * @returns {{parJour: Object<string, number>, jours: number, prevu: number, sup: number, moins: number}}
 */
export function bilanDuMois(lignes, emp) {
  const heures = {};
  (lignes || []).forEach((l) => {
    if (!l.k || !(+l.h > 0)) return;
    heures[l.k] = (heures[l.k] || 0) + +l.h;
  });
  const parJour = {};
  let sup = 0;
  let moins = 0;
  let jours = 0;
  Object.entries(heures).forEach(([k, h]) => {
    // Le contrat prévoit du lundi au vendredi : le samedi et le dimanche ne
    // sont pas des jours prévus, leurs heures sont toutes en plus.
    const ouvrable = estJourOuvrable(k);
    const e = ouvrable ? ecartDuJour(h, emp) : Math.round(h * 100) / 100;
    parJour[k] = e;
    if (ouvrable) jours += 1;
    if (e > 0) sup += e;
    else moins -= e;
  });
  return {
    parJour,
    jours,
    prevu: Math.round(jours * heuresDuContrat(emp) * 100) / 100,
    sup: Math.round(sup * 100) / 100,
    moins: Math.round(moins * 100) / 100,
  };
}

/**
 * Ce que l'employé lit en pointant son départ : ses heures du jour, celles
 * de son contrat, et l'écart. « 9h30 — contrat 8h30 : +1h00 d'heures sup ».
 *
 * @param {string} entree heure d'arrivée, `HH:MM`
 * @param {string} sortie heure de départ, `HH:MM`
 * @param {object} [emp] employé, dont le contrat fixe la journée et la pause
 * @param {{entree: string, sortie: string, pause?: number}[]} [dejaFaits] morceaux de la
 *   journée déjà refermés, sur d'autres chantiers
 * @returns {string} la phrase, vide si l'horaire ne se lit pas
 */
export function phraseDuDepart(entree, sortie, emp, dejaFaits = []) {
  // La journée entière, chantiers précédents compris : quitter le chantier A
  // à midi n'est pas « parti plus tôt ».
  const h =
    (dejaFaits || []).reduce(
      (t, s) => t + (s.entree && s.sortie ? hours(s.entree, s.sortie, Number(s.pause) || 0) : 0),
      0,
    ) + hours(entree, sortie, pauseDuSegment(dejaFaits, entree, sortie, emp));
  if (!(h > 0)) return '';
  const e = ecartDuJour(h, emp);
  const fmt = (x) => Math.floor(x) + 'h' + String(Math.round((x % 1) * 60)).padStart(2, '0');
  const suite =
    e > 0
      ? fmtEcart(e) + ' d’heures sup'
      : e < 0
        ? fmtEcart(e) + ', parti plus tôt'
        : 'journée complète';
  return fmt(h) + ' — contrat ' + fmt(heuresDuContrat(emp)) + ' : ' + suite;
}

/**
 * Les heures déjà pointées d'un employé un jour donné, sur tous ses
 * chantiers et hors chantier (dépôt, atelier).
 *
 * @param {object[]} chantiers chantiers, avec leurs pointages
 * @param {object} emp fiche de l'employé (ses pointages hors chantier dans `badges`)
 * @param {string} jour date du jour, telle que les pointages la portent
 * @returns {Array<{id: *, entree: string, sortie: string, chNom: string}>} les pointages du jour
 */
export function pointagesDuJour(chantiers, emp, jour) {
  if (!emp) return [];
  return [
    ...(chantiers || []).flatMap((c) =>
      (c.pointages || [])
        .filter((p) => p.empId === emp.id && p.date === jour && p.type !== 'absence')
        .map((p) => ({ ...p, chId: c.id, chNom: c.nom || '' })),
    ),
    ...(emp.badges || [])
      .filter((b) => b.date === jour && b.chId == null && b.type !== 'absence')
      .map((b) => ({ ...b, chNom: '' })),
  ];
}

/**
 * Le pointage du jour qui couvre déjà ces heures, s'il y en a un.
 *
 * <p>Le patron avait pointé cinq fois la même journée, 07:00 → 16:30 : chaque
 * pointage comptait, et la journée en faisait quarante. Une arrivée qui tombe
 * dans des heures déjà pointées, ou une journée qui en recouvre une autre, est
 * maintenant refusée. Deux chantiers le même jour, l'un après l'autre,
 * restent permis.
 *
 * @param {object[]} pointages pointages du jour (voir {@link pointagesDuJour})
 * @param {string} entree arrivée « HH:MM »
 * @param {string} [sortie] départ « HH:MM » ; sans départ, seule l'arrivée compte
 * @param {*} [sauf] identifiant du pointage en cours, à ne pas comparer à lui-même
 * @returns {object|null} le pointage recouvert, ou null
 */
export function pointageRecouvert(pointages, entree, sortie, sauf) {
  if (!/^\d{2}:\d{2}$/.test(entree || '')) return null;
  return (
    (pointages || []).find((p) => {
      if (sauf != null && p.id === sauf) return false;
      if (!p.entree) return false;
      const fin = p.sortie || '24:00';
      if (!sortie) return entree >= p.entree && entree < fin;
      return entree < fin && p.entree < sortie;
    }) || null
  );
}
